import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  type CitationCellAnchor,
  type FileCitation,
  type TextCitation,
  ticketMessageCitationSchema,
} from "@mg/schemas/src/commons/ticket";
import { z } from "zod";

export type TicketMessageCitation = z.infer<typeof ticketMessageCitationSchema>;

import { formatTimeStamp } from "../../../routes/tickets/components/VideoPlayback";

/** Payload for navigating the context pane to a cited source. */
export type CitationTarget = {
  pageNumber?: number;
  position?: { x: number; y: number };
  sourceUrl?: string;
  originalURL?: string;
  videoStart?: number;
  cellAnchor?: CitationCellAnchor;
  knowledgeDataRecordId?: string;
};

/** @deprecated Use `CitationTarget` — kept as alias for in-flight imports. */
export type ContextCitationTarget = CitationTarget;

export type ContextAssetRef = {
  source: string;
  name?: string;
};

export type ContextSourcePin = {
  relX: number;
  relY: number;
  page: number;
  sourceUrl: string;
  originalURL?: string;
  videoStart?: number;
  cellAnchor?: CitationCellAnchor;
};

export type DraftPin = { relX: number; relY: number; page: number };

export function isFileCitation(
  citation: TicketMessageCitation,
): citation is FileCitation {
  return (
    "url" in citation ||
    "originalURL" in citation ||
    "rel_x" in citation ||
    "videoStart" in citation ||
    "cellAnchor" in citation
  );
}

export function isTextCitation(
  citation: TicketMessageCitation,
): citation is TextCitation {
  return "text" in citation && !("url" in citation);
}

export function isKnowledgeDbCitation(
  citation: TicketMessageCitation,
): citation is { knowledgeDataRecordId: string } {
  return "knowledgeDataRecordId" in citation;
}

/** A synthesized Knowledge Database rule (`knowledgerules`) cited on a review
 * comment. `ruleId` is unique to this variant across the citation union, so it
 * is a safe discriminant. The citation carries its own `quote` so the chip is
 * self-describing without a follow-up fetch. */
export function isKnowledgeRuleCitation(
  citation: TicketMessageCitation,
): citation is Extract<TicketMessageCitation, { ruleId: string }> {
  return "ruleId" in citation;
}

/**
 * KDB-rule citations to render as chips: keep only the rule variant and collapse
 * ones that would render identically. The chip label is the citation's `quote`,
 * so two different rules backed by the same evidence line (common — one source
 * sentence can support several rules) show up as duplicate links. Dedupe on the
 * visible quote (trimmed, case-insensitive), falling back to `ruleId` when a
 * citation has no quote. First occurrence wins, so ordering is stable.
 */
export function dedupeRuleCitations(
  citations: readonly TicketMessageCitation[],
): Extract<TicketMessageCitation, { ruleId: string }>[] {
  const seen = new Set<string>();
  const out: Extract<TicketMessageCitation, { ruleId: string }>[] = [];
  for (const citation of citations) {
    if (!isKnowledgeRuleCitation(citation)) continue;
    const key =
      citation.quote?.trim().toLowerCase() || `rule:${citation.ruleId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(citation);
  }
  return out;
}

export function isNavigableFileCitation(
  citation: TicketMessageCitation,
): citation is FileCitation {
  return (
    isFileCitation(citation) &&
    ("originalURL" in citation ||
      "rel_x" in citation ||
      "videoStart" in citation ||
      "cellAnchor" in citation ||
      Boolean(citation.url))
  );
}

export function buildContextFileCitation(
  asset: ContextAssetRef,
  pin: ContextSourcePin,
): FileCitation {
  const citation: FileCitation = {
    url: pin.sourceUrl || asset.source,
    pageNumber: pin.page,
  };
  if (pin.originalURL) citation.originalURL = pin.originalURL;
  if (pin.relX != null && pin.relY != null) {
    citation.rel_x = pin.relX;
    citation.rel_y = pin.relY;
  }
  if (pin.videoStart != null) citation.videoStart = pin.videoStart;
  if (pin.cellAnchor) citation.cellAnchor = pin.cellAnchor;
  return citation;
}

export function mergeCitations(
  existing: TicketMessageCitation[] | undefined,
  added: TicketMessageCitation[],
): TicketMessageCitation[] {
  return [...(existing ?? []), ...added];
}

export function removeCitationAt(
  citations: TicketMessageCitation[],
  index: number,
): TicketMessageCitation[] {
  return citations.filter((_, i) => i !== index);
}

function truncate(text: string, maxLen = 48): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen - 1)}…`;
}

function cellAnchorLabel(anchor: CitationCellAnchor): string {
  const sheet =
    anchor.sheetName?.trim() ||
    (anchor.sheetIndex != null ? `Sheet ${anchor.sheetIndex + 1}` : "Sheet");
  if (anchor.range) return `${sheet} ${anchor.range}`;
  if (anchor.row != null) return `${sheet} row ${anchor.row}`;
  return sheet;
}

export function citationLabel(citation: TicketMessageCitation): string {
  if (isKnowledgeRuleCitation(citation)) {
    return citation.quote?.trim()
      ? truncate(citation.quote)
      : "Knowledge Database rule";
  }
  if (isKnowledgeDbCitation(citation)) return "Knowledge DB";
  if (isTextCitation(citation)) return truncate(citation.text);
  if (isFileCitation(citation)) {
    if (citation.videoStart != null) {
      return formatTimeStamp(citation.videoStart);
    }
    if (citation.cellAnchor != null) {
      return cellAnchorLabel(citation.cellAnchor);
    }
    return "Source";
  }
  return "Source";
}

export function fileCitationToTarget(citation: {
  url?: string;
  pageNumber?: number;
  originalURL?: string;
  rel_x?: number;
  rel_y?: number;
  videoStart?: number;
  cellAnchor?: CitationCellAnchor;
}): CitationTarget {
  const page = citation.pageNumber ?? 1;
  const pos =
    citation.rel_x != null && citation.rel_y != null
      ? { x: citation.rel_x, y: citation.rel_y }
      : undefined;
  return {
    pageNumber: page,
    position: pos,
    sourceUrl: citation.url,
    originalURL: citation.originalURL,
    videoStart: citation.videoStart,
    cellAnchor: citation.cellAnchor,
  };
}

/** File citations first, then knowledge-DB records (text citations are not navigable). */
export function getNavigableCitations(
  comment: BaseTicketComment,
): TicketMessageCitation[] {
  const citations = Array.isArray(comment.citations) ? comment.citations : [];
  const fileCitations = citations.filter(isNavigableFileCitation);
  const kbCitations = citations.filter(isKnowledgeDbCitation);
  return [...fileCitations, ...kbCitations];
}

export function citationToTarget(
  citation: TicketMessageCitation,
): CitationTarget | null {
  if (isKnowledgeDbCitation(citation)) {
    return { knowledgeDataRecordId: citation.knowledgeDataRecordId };
  }
  if (isNavigableFileCitation(citation)) {
    return fileCitationToTarget(citation);
  }
  return null;
}
